import { Navigate, useParams } from 'react-router-dom';

/**
 * The AccountDetail tab a retired account sub-page URL now lands on.
 *
 * Story #22201 (BR-MOB-013) — every account sub-page renders as a tab under the account header,
 * the pattern Bug #21730 established for Departments. Each old page URL maps to exactly one tab.
 */
export type AccountSubpageTab =
  | 'contacts'
  | 'departments'
  | 'portal-users'
  | 'connected'
  | 'm365-users'
  | 'licenses'
  | 'security';

// Generalises the Bug #21730 departments redirect (BR-DEPT-UI-005) to every account sub-page.
// The old URL redirects to the tab rather than 404ing, so existing bookmarks and links still land
// on the same content. `replace` keeps the retired URL out of the history stack, so Back does not
// bounce the user through the redirect again. `useParams` hands back a decoded value, so it is
// re-encoded here.
//
// The account routes name their id param inconsistently (`:id` on the older routes, `:accountId`
// on the later ones), so both are read rather than renaming any route.
//
// Role gates are NOT re-implemented here: AccountDetail already coerces a `?tab=` the caller may
// not see to the Overview tab (Bug #16262 / #16803), so a redirect to a gated tab lands on the
// account overview — never on a 403 page.
export default function AccountSubpageRedirect({ tab }: { tab: AccountSubpageTab }) {
  const params = useParams<{ id?: string; accountId?: string }>();
  const id = params.id ?? params.accountId ?? '';
  return <Navigate to={`/accounts/${encodeURIComponent(id)}?tab=${tab}`} replace />;
}
